import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import FilterBar from './components/FilterBar';
import { ARTICLES } from '@/mocks/articles';
import { CATEGORIES } from '@/mocks/categories';

export default function Archive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCat, setActiveCat] = useState(searchParams.get('category') || 'all');
  const [keyword, setKeyword] = useState(searchParams.get('q') || '');

  useEffect(() => {
    setActiveCat(searchParams.get('category') || 'all');
    setKeyword(searchParams.get('q') || '');
  }, [searchParams]);

  const currentCategory = CATEGORIES.find((cat) => cat.id === activeCat);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return ARTICLES.filter((article) => {
      const matchCat = activeCat === 'all' || article.categoryId === activeCat;
      const matchKw =
        kw === '' ||
        [article.title, article.excerpt, article.id, article.author, ...article.tags]
          .join(' ')
          .toLowerCase()
          .includes(kw);
      return matchCat && matchKw;
    }).sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [activeCat, keyword]);

  const updateParams = (cat: string, kw: string) => {
    const next = new URLSearchParams();
    if (cat !== 'all') next.set('category', cat);
    if (kw.trim() !== '') next.set('q', kw.trim());
    setSearchParams(next, { replace: true });
  };

  const handleCategory = (id: string) => {
    setActiveCat(id);
    updateParams(id, keyword);
  };

  const handleReset = () => {
    setActiveCat('all');
    setKeyword('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '黑料百科考古档案库',
        description:
          '黑料百科考古档案库按早期论坛、被删档案、小众亚文化、尘封老游戏、消失的网站与迷因考古六大专题，收录冷门、小众、被删与尘封的网络内容记录。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '考古档案', item: '/archive' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: filtered.map((article, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: article.title,
          url: `/archive/${article.slug}`,
        })),
      },
    ],
    [filtered],
  );

  return (
    <>
      <Seo
        title="考古档案库 | 黑料百科 · 冷门小众被删内容索引"
        description="按早期论坛、被删档案、小众亚文化、尘封老游戏、消失的网站与迷因考古六大专题，翻阅黑料百科整理的网络内容考古记录，支持关键词检索与专题筛选。"
        keywords="考古档案,黑料百科,冷门内容,被删内容,早期论坛,消失的网站,尘封"
        path="/archive"
        ogType="website"
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="w-full pt-[68px] md:pt-[74px]">
        <section className="w-full border-b border-background-200 bg-background-100 archive-grain">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '考古档案' }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
              {currentCategory ? `${currentCategory.name}档案` : '考古档案库'}
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
              {currentCategory
                ? currentCategory.desc
                : '这里是全部已入库的考据记录。每一条都标有档案编号、材料来源与复原程度，可以按专题筛选，也可以直接搜索关键词。'}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs text-foreground-600">
              <span className="flex items-center gap-2">
                <i className="ri-archive-2-line text-primary-500"></i>
                已入库 {ARTICLES.length} 条记录
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-price-tag-3-line text-primary-500"></i>
                覆盖 {CATEGORIES.length} 个专题
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-primary-500"></i>
                全部标注材料状态
              </span>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-8">
            <FilterBar
              activeCat={activeCat}
              keyword={keyword}
              resultCount={filtered.length}
              total={ARTICLES.length}
              onCategoryChange={handleCategory}
              onKeywordChange={setKeyword}
              onReset={handleReset}
            />

            {filtered.length > 0 ? (
              <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filtered.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100/60 py-20 flex flex-col items-center text-center px-6">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 text-2xl">
                  <i className="ri-file-search-line"></i>
                </span>
                <h2 className="mt-5 font-heading text-lg text-foreground-900">
                  这条线索还没被挖到
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-600 leading-relaxed">
                  没有匹配的档案记录。你可以换个关键词，或者重置筛选后从全部记录里翻一翻。
                </p>
                <button
                  type="button"
                  onClick={handleReset}
                  className="mt-6 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}